/**
 * Every API path the web app calls, in one place, with its typed contract. D1 owns the
 * API; when a path or shape there differs, change it here (and the type in ./types.ts)
 * only. Convention (BUILD-PLAN): the /api/v1 prefix is added by api(); list endpoints
 * accept ?page&pageSize&q&sort plus filters as comma lists and return Page<T>. The
 * organisation comes from the session — the client never sends an orgId for scoping.
 *
 * Status: written against BUILD-PLAN.md before D1's API landed. See docs/sprints/D2.md
 * "Open API assumptions" for the list to confirm.
 */
import type { Page } from './api';
import type {
  Cabinet,
  DocumentSummary,
  DocumentType,
  Folder,
  HomeSummary,
  LoginResponse,
  Notification,
  PathPart,
  QueueSummary,
  RecentSearch,
  Ref,
  SavedSearch,
  SearchHit,
  SessionResponse,
  TagRef,
} from './types';

const id = (x: string) => encodeURIComponent(x);

export const EP = {
  auth: {
    login: '/auth/login',
    mfa: '/auth/mfa',
    mfaSetup: '/auth/mfa/setup',
    mfaEnable: '/auth/mfa/enable',
    password: '/auth/password',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    me: '/auth/me',
    reissue: '/auth/reissue',
    /** Switch the session's organisation (body { orgId }) → SessionResponse. */
    switchOrg: '/auth/org',
  },
  settingsPublic: '/settings/public',
  home: '/dashboard/home',
  homePreferences: '/dashboard/preferences',
  queue: '/queue/summary',
  notifications: '/notifications',
  notificationsRead: '/notifications/read',
  search: '/search',
  searches: { recent: '/searches/recent', saved: '/searches/saved' },
  documents: {
    list: '/documents',
    /** Documents this user opened most recently. */
    recentlyOpened: '/documents/recently-opened',
    one: (docId: string) => `/documents/${id(docId)}`,
    thumbnail: (docId: string) => `/documents/${id(docId)}/thumbnail`,
  },
  inbox: '/inbox',
  trash: '/trash',
  cabinets: '/cabinets',
  /** Children of a cabinet (parentId omitted) or of a folder. */
  folders: (cabinetId: string) => `/cabinets/${id(cabinetId)}/folders`,
  folderPath: (folderId: string) => `/folders/${id(folderId)}/path`,
  documentTypes: '/document-types',
  tags: '/tags',
  correspondents: '/correspondents',
  users: '/users',
  /** Everyone in the organisation (id, name, email) — any signed-in user (uploaded-by filter). */
  usersDirectory: '/users/directory',
  uploads: '/uploads',
  files: '/files',
  file: (fileId: string) => `/files/${id(fileId)}`,
} as const;

/** Query for every document list (documents, inbox, trash, cabinet contents, search). */
export interface DocumentQuery {
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  cabinet?: string;
  folder?: string;
  type?: string;
  tag?: string;
  correspondent?: string;
  status?: string;
  uploadedBy?: string;
  /** Date range: a preset (7d, 30d, 12m, year) or YYYY-MM-DD..YYYY-MM-DD. */
  date?: string;
  exact?: boolean;
}

/**
 * The typed contract: request → response for each call the shell makes. Pages use these
 * through api<Res<'…'>>(…) so a shape change shows up as a type error.
 */
export interface Contract {
  'POST /auth/login': { body: { login: string; password: string }; res: LoginResponse };
  'POST /auth/mfa': { body: { mfaToken: string; code: string }; res: SessionResponse };
  'POST /auth/org': { body: { orgId: string | null }; res: SessionResponse };
  'GET /settings/public': { res: { brandName?: string; idleTimeoutMinutes?: number } };
  'GET /users/directory': { res: { id: string; name: string; email?: string }[] };
  'GET /dashboard/home': { res: HomeSummary };
  'GET /queue/summary': { res: QueueSummary };
  'GET /notifications': { res: Page<Notification> };
  'GET /search': { query: DocumentQuery; res: Page<SearchHit> & { took?: number } };
  'GET /searches/recent': { res: RecentSearch[] };
  'GET /searches/saved': { res: SavedSearch[] };
  'GET /documents': { query: DocumentQuery; res: Page<DocumentSummary> };
  'GET /documents/recently-opened': { res: DocumentSummary[] };
  'GET /inbox': { query: DocumentQuery; res: Page<DocumentSummary> };
  'GET /trash': { query: DocumentQuery; res: Page<DocumentSummary> };
  'GET /cabinets': { res: Cabinet[] };
  'GET /cabinets/:id/folders': { query: { parentId?: string }; res: Folder[] };
  'GET /folders/:id/path': { res: PathPart[] };
  'GET /document-types': { res: DocumentType[] };
  'GET /tags': { res: TagRef[] };
  'GET /correspondents': { res: Ref[] };
  'GET /users': { query: { pageSize?: number }; res: Page<Ref> };
}
export type Res<K extends keyof Contract> = Contract[K]['res'];
